import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowLeft,
    CheckCircle,
    Clock,
    TrendingUp,
    AlertCircle,
    Download,
    Share,
    Edit,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { goalApi } from "../../../api/goalApi";
import { formatDate } from "../../../utils/stringUtils";
import { usePerformanceCycles } from "../../../hooks/usePerformanceCycles";

const GoalDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const {
        data: goalResponse,
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: ["goal", id],
        queryFn: async () => {
            const response = await goalApi.getGoalById(id);
            return response.data || response;
        },
        enabled: !!id,
    });

    // Fetch cycles to resolve cycle name if needed
    const { data: cyclesResponse } = usePerformanceCycles({ excludeClosed: false }); // Fetch all cycles including closed
    const cycles = cyclesResponse?.data?.cycles || cyclesResponse?.cycles || [];

    const goal = goalResponse;

    const handleBack = () => {
        navigate(-1); // Navigate back to Goal Management list
    };


    const getStatusColor = (status) => {
        switch (status) {
            case "not_started":
                return "bg-gray-100 text-gray-800";
            case "in_progress":
                return "bg-blue-100 text-blue-800";
            case "completed":
                return "bg-green-100 text-green-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "not_started":
                return <Clock className="h-4 w-4" />;
            case "in_progress":
                return <TrendingUp className="h-4 w-4" />;
            case "completed":
                return <CheckCircle className="h-4 w-4" />;
            default:
                return <Clock className="h-4 w-4" />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case "high":
                return "bg-red-100 text-red-800";
            case "medium":
                return "bg-yellow-100 text-yellow-800";
            case "low":
                return "bg-green-100 text-green-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
            </div>
        );
    }

    if (isError || !goal) {
        return (
            <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Goal</h3>
                <p className="text-gray-500 mb-4">{error?.message || "Goal not found"}</p>
                <Button onClick={handleBack} variant="outline">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Goals
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <Button variant="outline" size="sm" onClick={handleBack} icon={<ArrowLeft className="h-4 w-4" />}>
                        Back
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Goal Details</h1>
                        <p className="text-gray-600">View performance goal information</p>
                    </div>
                </div>
                {/* Actions could go here */}
            </div>

            {/* Goal Content Reused from RenderViewFields */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
                {/* Title Section */}
                <div className="bg-gradient-to-r from-agrosaf-green/10 to-agrosaf-green/5 rounded-lg p-4 border border-agrosaf-green/20">
                    <h3 className="text-xl font-bold text-gray-900 break-words">{goal.title || "N/A"}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
                            {getStatusIcon(goal.status)}
                            <span className="ml-1 capitalize">
                                {goal.status ? goal.status.replace("_", " ") : "N/A"}
                            </span>
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(goal.priority)}`}>
                            {goal.priority || "N/A"}
                        </span>
                    </div>
                </div>

                {/* Main Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Employee
                        </label>
                        <div className="space-y-1 break-words">
                            <p className="text-sm font-medium text-gray-900 break-words">{goal.employee || "N/A"}</p>
                            {goal.employeeId && (
                                <p className="text-xs text-gray-500 mt-0.5 break-words">ID: {goal.employeeId}</p>
                            )}
                            {goal.employeeEmail && (
                                <p className="text-xs text-gray-500 mt-0.5 break-words">{goal.employeeEmail}</p>
                            )}
                            {goal.employeeDepartment && (
                                <p className="text-xs text-gray-500 mt-0.5 break-words">Dept: {goal.employeeDepartment}</p>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Manager
                        </label>
                        <div className="space-y-1 break-words">
                            <p className="text-sm font-medium text-gray-900 break-words">{goal.manager || "N/A"}</p>
                            {goal.managerId && (
                                <p className="text-xs text-gray-500 mt-0.5 break-words">ID: {goal.managerId}</p>
                            )}
                            {goal.managerEmail && (
                                <p className="text-xs text-gray-500 mt-0.5 break-words">{goal.managerEmail}</p>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Date
                        </label>
                        <p className="text-sm text-gray-900">
                            {goal.startDate ? formatDate(goal.startDate, { includeTime: false }) : "N/A"}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Date
                        </label>
                        <p className="text-sm text-gray-900">
                            {goal.endDate ? formatDate(goal.endDate, { includeTime: false }) : "N/A"}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Progress
                        </label>
                        <div className="flex items-center space-x-3">
                            <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                                <div
                                    className="bg-agrosaf-green h-2.5 rounded-full transition-all"
                                    style={{ width: `${Number(goal.progress || 0)}%` }}
                                ></div>
                            </div>
                            <span className="text-sm font-medium text-gray-900 min-w-[40px] text-right">{Number(goal.progress || 0)}%</span>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cycle
                        </label>
                        <p className="text-sm text-gray-900 break-words">
                            {Array.isArray(cycles) && cycles.length > 0
                                ? cycles.find((cycle) => cycle.id === goal.cycle)?.cycleName ||
                                goal.cycle ||
                                "N/A"
                                : goal.cycle || "N/A"}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Last Updated
                        </label>
                        <p className="text-sm text-gray-900 break-words">
                            {goal.lastUpdated
                                ? formatDate(goal.lastUpdated, { includeTime: false })
                                : goal.updatedAt
                                    ? formatDate(goal.updatedAt, { includeTime: false })
                                    : "N/A"}
                        </p>
                    </div>
                    {goal.remark && (
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Remark
                            </label>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap break-words bg-gray-50 rounded-lg p-3 border border-gray-200">
                                {goal.remark}
                            </p>
                        </div>
                    )}
                    {goal.status === 'completed' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Completed Date
                            </label>
                            <p className="text-sm text-gray-900">
                                {goal.completedAt
                                    ? formatDate(goal.completedAt, { includeTime: false })
                                    : goal.updatedAt
                                        ? formatDate(goal.updatedAt, { includeTime: false })
                                        : "N/A"}
                            </p>
                        </div>
                    )}
                </div>

                {/* Description Section */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                    </label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed">
                        {goal.description || "N/A"}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default GoalDetail;
